import axios from 'axios';
import _ from 'lodash'
import {quantile} from 'd3-array'
import {themeQuartz} from "ag-grid-enterprise";
import { getBusinessDays } from '@/services/api/tasks';

// Convert hex to RGB
export function hexToRgb(hex) {
  let r = 0,
    g = 0,
    b = 0;
  // remove '#' if present
  if (hex.startsWith('#')) {
    hex = hex.slice(1);
  }
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  }
  return { r, g, b };
}

// Convert RGB to hex
export function rgbToHex(r, g, b) {
  r = Math.round(r).toString(16);
  g = Math.round(g).toString(16);
  b = Math.round(b).toString(16);
  return (
    '#' +
    (r.length === 1 ? '0' + r : r) +
    (g.length === 1 ? '0' + g : g) +
    (b.length === 1 ? '0' + b : b)
  );
}

export function getRandomColor() {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

// Convert RGB to HSV
export function rgbToHsv(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  let max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    v = max;

  let d = max - min;
  s = max === 0 ? 0 : d / max;

  if (max === min) {
    h = 0; // achromatic
  } else {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h, s, v };
}

// Convert HSV to RGB
// Helper function to convert HSV to RGB
export function hsvToRgb(h, s, v) {
  let r, g, b, i, f, p, q, t;
  i = Math.floor(h * 6);
  f = h * 6 - i;
  p = v * (1 - s);
  q = v * (1 - f * s);
  t = v * (1 - (1 - f) * s);

  switch (i % 6) {
    case 0:
      (r = v), (g = t), (b = p);
      break;
    case 1:
      (r = q), (g = v), (b = p);
      break;
    case 2:
      (r = p), (g = v), (b = t);
      break;
    case 3:
      (r = p), (g = q), (b = v);
      break;
    case 4:
      (r = t), (g = p), (b = v);
      break;
    case 5:
      (r = v), (g = p), (b = q);
      break;
  }

  return { r: r * 255, g: g * 255, b: b * 255 };
}

function hslToRgb(h, s, l) {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hh = h / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r1 = 0,
    g1 = 0,
    b1 = 0;

  if (0 <= hh && hh < 1) {
    r1 = c;
    g1 = x;
    b1 = 0;
  } else if (1 <= hh && hh < 2) {
    r1 = x;
    g1 = c;
    b1 = 0;
  } else if (2 <= hh && hh < 3) {
    r1 = 0;
    g1 = c;
    b1 = x;
  } else if (3 <= hh && hh < 4) {
    r1 = 0;
    g1 = x;
    b1 = c;
  } else if (4 <= hh && hh < 5) {
    r1 = x;
    g1 = 0;
    b1 = c;
  } else if (5 <= hh && hh < 6) {
    r1 = c;
    g1 = 0;
    b1 = x;
  }

  const m = l - c / 2;
  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);

  return { r, g, b };
}

export function toProperCase(str) {
  if (str) {
    return str.replace(/\w\S*/g, txt => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  } else {
    return '';
  }
}

export function generateSimilarColor(
  hex,
  variance = 0.1,
  brightnessAdjustment = 0
) {
  let { r, g, b } = hexToRgb(hex);
  let { h, s, v } = rgbToHsv(r, g, b);
  // Randomly adjust hue, saturation, and brightness
  h += Math.random() * variance - variance / 2; // Wrap around the hue circle
  if (h < 0) {
    h += 1;
  }
  if (h > 1) {
    h -= 1;
  }

  s += Math.random() * variance - variance / 2;
  s = Math.max(0, Math.min(1, s));

  v += brightnessAdjustment / 255;
  v = Math.max(0, Math.min(1, v));

  // Convert back to RGB, then to Hex
  let rgb = hsvToRgb(h, s, v);

  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

export function progressColor(
  value,
  lower_bound,
  upper_bound,
  reverse = false,
  alpha = 1.0,
  lightness = 50,
  saturation = 100
) {
  const progressColor = returnProgressColorRGB(
    value,
    lower_bound,
    upper_bound,
    reverse,
    lightness,
    saturation
  );
  return RGBAtoHTML(progressColor, alpha);
}

export function returnProgressColorRGB(
  value,
  lower_bound,
  upper_bound,
  reverse = false,
  lightness,
  saturation
) {
  if (value !== undefined && value !== null) {
    // Compute percentage between 0 and 1
    let percentage = (value - lower_bound) / (upper_bound - lower_bound);
    // Clamp percentage between 0 and 1
    percentage = Math.max(0, Math.min(1, percentage));

    // Calculate hue from 0 (red) to 120 (green)
    let hue;
    if (reverse) {
      hue = (1 - percentage) * 120;
    } else {
      hue = percentage * 120;
    }

    const hsl_color = { h: hue, s: saturation, l: lightness };
    let rgb_color = hslToRgb(hsl_color.h, hsl_color.s, hsl_color.l);
    return rgb_color;
  } else {
    return { r: 100, g: 100, b: 100 }; // Default gray background for undefined or null values
  }
}

export function RGBAtoHTML(rgbaColor, alpha) {
  if (
    rgbaColor.r !== undefined &&
    rgbaColor.g !== null &&
    rgbaColor.b !== null &&
    alpha !== null
  ) {
    const rgbahtml = `rgba(${rgbaColor.r}, ${rgbaColor.g}, ${rgbaColor.b}, ${alpha})`;
    return rgbahtml;
  } else {
    return 'bg-gray-300'; // Default gray background for undefined or null values
  }
}

export function changeAlpha(hex, alpha) {
  let cleanHex = hex.replace('#', '');
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(c => c + c).join('');
  }
  if (cleanHex.length !== 6) {
    throw new Error('Invalid HEX color.');
  }
  const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0');
  return `#${cleanHex}${alphaHex}`;
}

export function stringToHexColor(input) {
  const pastelColors = [
    '#FF7E79',
    '#FFCC87',
    '#FFD776',
    '#8FD088',
    '#87B9F2',
    '#C084D1',
    '#7A4A76',
    '#FFB394',
    '#FFB8B8',
  ];

  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % pastelColors.length;
  return pastelColors[index];
}

export function stringToRgbaColor(input, alpha = 1) {
  const hex = stringToHexColor(input);
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function formatValue(value, fractionDigits, formatting) {
  if (typeof value !== 'number' || isNaN(value)) {
    return null;
  } else {
    if (formatting) {
      if (formatting.includes('%')) {
        return `${(value * 100).toFixed(fractionDigits)}%`;
      } else if (formatting.includes('$')) {
        const valueInThousands = value / 1000;
        const roundedValue = Number(valueInThousands.toPrecision(3));
        return `$${roundedValue}k`;
      }
    } else {
      return `${value.toFixed(fractionDigits)}`;
    }
  }
}

export function formatCurrency(value) {
  if (typeof value !== 'number') {
    console.error('formatCurrency method expects a number');
    return value; // Return the input as-is if it's not a number
  }

  // Formatter for USD currency
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0, // Ensures that there are always two decimal places
  });

  return formatter.format(value);
}

export function navigateToPerformance(
  object_id,
  content_type_model,
  performance_domain_id,
  metric_id,
  $router
) {
  $router.push({
    name: 'Metric Details',
    params: {
      object_id: object_id,
      content_type_model: content_type_model,
      performance_domain_id: performance_domain_id,
      metric_id: metric_id,
    },
  });
}

export const CONTENT_TYPE_MODEL_TO_ROUTE = {
  appuser: {
    routeName: 'User',
    routeParam: 'user_id',
  },
  engagement: {
    routeName: 'Engagement',
    routeParam: 'engagement_id',
  },
  contract: {
    routeName: 'Contract',
    routeParam: 'contract_id',
  },
  workflow: {
    routeName: 'Workflow',
    routeParam: 'workflow_id',
  },
};

export function goToObject(content_type_model, object_id, $router) {
  const routeInfo = CONTENT_TYPE_MODEL_TO_ROUTE[content_type_model];

  if (routeInfo) {
    const { routeName, routeParam } = routeInfo;
    const newRoute = {
      name: routeName,
      params: {
        [routeParam]: object_id,
      },
    };

    const currentRoute = $router.currentRoute.value;

    if (
      currentRoute.name === routeName
    ) {
      // Force a full reload by setting window.location.href
      window.location.href = $router.resolve(newRoute).href;
    } else {
      // Navigate normally if the route or params are different
      $router.push(newRoute);
    }
  }
}

// Helper function to calculate the metric value
const calculateMetric = (group, skip_denom=false) => {
  const sumNumerator = _.sumBy(group, (item) => item.numerator || 0);

  if(skip_denom){
    return sumNumerator
  }
  else{
    const sumDenominator = _.sumBy(group, (item) => item.denominator || 0);
    return sumDenominator !== 0 ? sumNumerator / sumDenominator : null;
  }
};

// Helper function to calculate the average
const calculateAverage = (values) => {
  return values.length > 0 ? _.mean(values) : null;
};

// Helper function to calculate a specific quartile
const calculateQuartile = (dataArray, dependentVar,depVarValue, objectKey,cohort_key, cohort_id, percentile) => {
  const filtered = dataArray.filter(
    (item) => item[dependentVar] === depVarValue && item[cohort_key] === cohort_id
  );

  const groupedByObject = _.groupBy(filtered, objectKey);

  const values = Object.values(groupedByObject)
    .map(calculateMetric)
    .filter((value) => value !== null);

  return values.length > 0 ? quantile(values.sort((a, b) => a - b), percentile) : null;
};

export function return_object_data_series(
    dataArray,
    dependentVar,
    objectKey,
    objectId,
    skip_denom = false
){

  const unique_dependent_vars = [...new Set(dataArray.map(item => item[dependentVar]))].sort();

  return {
    'labels': unique_dependent_vars,
    'values': unique_dependent_vars.map((dep) => {
      const filtered = dataArray.filter(
          (item) =>
              item[dependentVar] === dep &&
              item[objectKey] === objectId
      );
      if (filtered.length === 0) return {[dep]: null};
      return calculateMetric(filtered,skip_denom);
    })
  }
}

export function return_simple_data_series(
    dataArray,
    dependentVar,
    independentVar,
    filters = null) {

  let filteredArray = dataArray;

  // Apply filtering if filters object is provided
  if (filters && typeof filters === 'object') {
    filteredArray = dataArray.filter(item => {
      // Check if the item matches all filter conditions
      return Object.entries(filters).every(([filterVar, filterValue]) => {
        return filterValue !== undefined && filterValue !== null &&
               item[filterVar] === filterValue;
      });
    });
  }

  const unique_dependent_vars = [...new Set(filteredArray.map(item => item[dependentVar]))].sort();

  return {
    'labels': unique_dependent_vars,
    'values': unique_dependent_vars.map((dep) => {
      // sum the independent variable for each dependent variable
      const filtered = filteredArray.filter(item => item[dependentVar] === dep);
      return _.sumBy(filtered, item => item[independentVar] || 0);
    })
  }
}

export function return_quartile(
  dataArray,
  dependentVar,
  objectKey,
  objectId,
  cohort_key,
  cohort_id,
  percentile
) {
  // Step 1: Get Sorted Unique Dependents
  const unique_dependent_vars = [...new Set(dataArray.map(item => item[dependentVar]))].sort();

  // Step 3: Calculate Quartiles
  return {
    'labels': unique_dependent_vars,
    'values': unique_dependent_vars.map((depVarValue) =>
        calculateQuartile(dataArray, dependentVar, depVarValue, objectKey, cohort_key, cohort_id, percentile)
    )
  }
}

export function return_average(
  dataArray,
  dependentVar,
  objectKey,
  objectId,
  cohort_key,
  cohort_id
) {
  // Get sorted unique dependents
  const unique_dependent_vars = [...new Set(dataArray.map(item => item[dependentVar]))].sort();

  // Calculate Averages
  return {
    'labels': unique_dependent_vars,
    'values': unique_dependent_vars.map((depVarValue) => {
      const filtered = dataArray.filter(
          (item) =>
              item[dependentVar] === depVarValue &&
              item[cohort_key] === cohort_id
      );
      const groupedByObject = _.groupBy(filtered, objectKey);

      const values = Object.values(groupedByObject)
          .map(item => calculateMetric(item,false))
          .filter((value) => value !== null);



      return calculateAverage(values);
    })
  }
}


export const standardListTheme = themeQuartz.withParams({
  headerFontSize: 13,
  fontSize: 12,
  wrapperBorderRadius: 0,
  fontFamily: "inherit",
  rowBorder: true,
  rowBorderColor: 'rgb(243 244 246)',
  wrapperBorder: false,
  headerBackgroundColor: '#F8F8F8',
  headerColumnBorder:false,
  headerFontColor: 'rgb(55 65 81)',
  rowHoverColor: 'rgb(220 220 220)',
  backgroundColor: 'white',
  alternateBackgroundColor: 'rgb(200 200 200)',
})

export const COST_CATEGORY_COLORS = {
  ip_cost: '#4367b0',
  op_cost: '#5b8fbd',
  snf_cost: '#c48532',
  hh_cost: '#7a82a6',
  hospice_cost: '#b06470',
  part_b_cost: '#5a967a',
  dme_cost: '#8c8f96',
}

export const COST_CATEGORIES = [
  { key: 'ip_cost', label: 'IP', color: '#4367b0' },
  { key: 'op_cost', label: 'OP', color: '#5b8fbd' },
  { key: 'snf_cost', label: 'SNF', color: '#c48532' },
  { key: 'hh_cost', label: 'Home Health', color: '#7a82a6' },
  { key: 'hospice_cost', label: 'Hospice', color: '#b06470' },
  { key: 'part_b_cost', label: 'Part B', color: '#5a967a' },
  { key: 'dme_cost', label: 'DME', color: '#6b7280' },
]

export const standardListThemeRounded = themeQuartz.withParams({
  headerFontSize: 12,
  fontSize: 12,
  wrapperBorderRadius: 5,
  fontFamily: "inherit",
  rowBorder: true,
  rowBorderColor: 'rgb(243 244 246)',
  wrapperBorder: false,
  headerBackgroundColor: '#F8F8F8',
  headerFontColor: 'rgb(55 65 81)',
  rowHoverColor: 'rgb(220 220 220)',
  backgroundColor: 'white',
  alternateBackgroundColor: 'rgb(200 200 200)',
})

export const compactListTheme = themeQuartz.withParams({
  headerFontSize: 12,
  fontFamily: "inherit",
  borders: 'none',
  rowBorder: true,
  rowBorderColor: 'rgb(243 244 246)',
  wrapperBorder: false,
  headerBackgroundColor: '#F8F8F8',
  headerFontColor: 'rgb(55 65 81)',
  rowHoverColor: 'rgb(220 220 220)',
  backgroundColor: 'white',
  alternateBackgroundColor: 'rgb(200 200 200)',
  borderRadius: 5,
  cellHorizontalPaddingScale: 0.5,
  fontSize: 12,
  spacing: 4,
  wrapperBorderRadius: 5
})


/**
 * Parse a date value into a Date. Date-only strings (YYYY-MM-DD) are parsed as
 * midnight UTC, which shifts to the previous day in US timezones, so anchor
 * them to local noon instead. Full timestamps and other values pass through.
 */
export function parseDate(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T12:00:00`);
  }
  return new Date(value);
}

/**
 * Format a ISO date/time string as a human-readable date in the browser's local time zone.
 * `locale` and `options` are passed to toLocaleDateString.
 */
export function formatDate(dateString, locale, options) {
  if (!dateString) return '';
  return parseDate(dateString).toLocaleDateString(locale, options);
}

// Format a ISO date/time string as a human-readable time in the browser's local time zone.
export function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString();
}

// Format a UTC due_time (e.g. "19:30:00Z") as local time ("2:30 PM").
// Requires the companion due_date so DST is handled correctly.
export function formatDueTime(timeStr, dateStr) {
  if (!timeStr || !dateStr) return '';
  const iso = `${dateStr}T${timeStr.replace(/Z$/i, '')}Z`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const hh = d.getHours();
  const mm = d.getMinutes();
  const period = hh >= 12 ? 'PM' : 'AM';
  const h = hh % 12 || 12;
  return `${h}:${String(mm).padStart(2, '0')} ${period}`;
}

// --------------------------------------------------------------------------
// BUSINESS DAYS
// --------------------------------------------------------------------------
// The backend ALREADY normalizes every due date it stores: each date-computing
// instruction handler runs its result through next_business_day() (weekends +
// a curated federal holiday set) before saving. Verified in production — of
// 7,990 tenant-8 tasks due since 2026-07-01, only 5 land on a weekend and all
// 5 are migration backfill rows, never a nurse click.
//
// So this helper is NOT here to fix scheduling. It exists so the date the
// nurse SEES matches the date that will be SAVED. The quick-fill buttons
// (1d/2d/3d/7d) previously did raw calendar arithmetic, so clicking "2d" on a
// Thursday displayed Saturday while the backend silently stored Monday. The
// nurse had no way to know the real due date without saving and reloading —
// which is what IHCI reported as "calendar day logic" (#2587).
//
// THE HOLIDAY LIST IS FETCHED, NOT DUPLICATED. Hard-coding it here would give
// us two lists that drift the first time one is edited and the other is not.
// GET /api/tasks/business-days/ serves the current tenant's holiday set
// (api/worklist/models.py::holidays_for_tenant). Switching tenants reloads the
// page, which clears this cache.
// Until it resolves we fall back to weekends-only, which is strictly better
// than raw arithmetic and never worse than the previous behaviour.

let _holidaySet = null;
let _holidayPromise = null;

export function loadBusinessDayHolidays() {
  if (_holidaySet) return Promise.resolve(_holidaySet);
  if (_holidayPromise) return _holidayPromise;
  _holidayPromise = getBusinessDays()
    .then(({ holidays }) => {
      _holidaySet = new Set(holidays);
      return _holidaySet;
    })
    .catch(() => {
      // Weekend-only fallback. Do not cache, so a later call can retry.
      _holidayPromise = null;
      return new Set();
    });
  return _holidayPromise;
}

// Push a Date forward to the next business day. Mirrors
// api/worklist/models.py::next_business_day exactly — same direction (always
// forward), same predicate (Sat/Sun or in the holiday set).
export function nextBusinessDay(date) {
  const d = new Date(date.getTime());
  const holidays = _holidaySet || new Set();
  const pad = n => String(n).padStart(2, '0');
  for (let i = 0; i < 30; i += 1) {
    const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    if (d.getDay() !== 0 && d.getDay() !== 6 && !holidays.has(iso)) return d;
    d.setDate(d.getDate() + 1);
  }
  return d;
}
